import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Card from './Card';
import Button from './Button';
import Loading, { Spinner } from './Loading';
import Alert from './Alert';
import { formatRelativeTime } from '../utils/helpers';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: 80vh;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${({ theme }) => theme.colors.background.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const MessageRow = styled.div`
  display: flex;
  flex-direction: ${({ $isUser }) => ($isUser ? 'row-reverse' : 'row')};
  align-items: flex-end;
  gap: 0.75rem;
  max-width: 85%;
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: white;
  background-color: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.primary.main : theme.colors.success.main};
  flex-shrink: 0;
`;

const MessageBubble = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${({ theme, $isUser }) =>
    $isUser
      ? `${theme.borderRadius.md} ${theme.borderRadius.md} 0 ${theme.borderRadius.md}`
      : `${theme.borderRadius.md} ${theme.borderRadius.md} ${theme.borderRadius.md} 0`};
  background-color: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.primary.light : theme.colors.background.card};
  color: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.primary.contrastText : theme.colors.text.primary};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  position: relative;
`;

const MessageText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageTime = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  color: ${({ theme, $isUser }) =>
    $isUser ? 'rgba(255, 255, 255, 0.7)' : theme.colors.text.tertiary};
  margin-top: 0.25rem;
  text-align: right;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const TextInput = styled.textarea`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border.main};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  resize: none;
  min-height: 2.5rem;
  max-height: 150px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.light};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SuggestionChip = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.main};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.hover};
    border-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;



/**
 * ChatInterface component for interacting with the AI agent
 * @param {Object} props
 * @param {Function} props.onSendMessage - Function to call when sending a message
 * @param {Array} props.messages - List of chat messages
 * @param {boolean} props.isTyping - Whether the agent is typing
 * @param {Array} props.suggestions - List of suggested questions
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
const ChatInterface = ({
  onSendMessage,
  messages = [],
  isTyping = false,
  suggestions = [],
  loading = false,
  error = null
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThinking, setShowThinking] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    console.log('ChatInterface: isTyping state changed:', isTyping);
    scrollToBottom();
  }, [messages, isTyping]);
  
  // Reset showThinking when new messages arrive
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].isUser === false) {
      // If the last message is from the agent, turn off thinking indicator
      setShowThinking(false);
    }
  }, [messages]);
  
  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const message = inputValue.trim();
    if (!message) return;
    
    setIsSubmitting(true);
    setShowThinking(true); // Show thinking indicator
    
    try {
      await onSendMessage(message);
      setInputValue('');
      
      // Reset textarea height
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
      // We'll keep showThinking true until a new message arrives
    }
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    
    // Focus the textarea
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.focus();
      
      // Auto-resize textarea
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  };
  
  // Handle key press
  const handleKeyPress = (e) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Default suggestions if none provided
  const defaultSuggestions = [
    "What's the optimal soil moisture for strawberries?",
    "How do I prevent fungal diseases in my strawberry plants?",
    "When is the best time to fertilize my strawberries?",
    "What are signs of pest infestation in strawberries?",
    "What's the best irrigation schedule during hot summer days?",
    "How can I maximize my strawberry yield this season?"
  ];
  
  // Use provided suggestions or defaults
  const displaySuggestions = suggestions.length > 0 ? suggestions : defaultSuggestions;

  return (
    <Card title="Ask the Farming Assistant">
      {loading ? (
        <Loading text="Loading chat history..." />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : (
        <ChatContainer>
          <MessagesContainer>
            {messages.length === 0 ? (
              <WelcomeMessage>
                <h3>👋 Kia Ora! Welcome to the AI Grower Assistant.</h3>
                <p>Ask me anything about strawberry farming, irrigation, pest control, or harvesting.</p>
                
                <Suggestions>
                  {displaySuggestions.map((suggestion, index) => (
                    <SuggestionChip 
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </SuggestionChip>
                  ))}
                </Suggestions>
              </WelcomeMessage>
            ) : (
              <>
                {messages.map((message, index) => (
                  <MessageRow key={index} $isUser={message.isUser}>
                    <Avatar $isUser={message.isUser}>
                      {message.isUser ? 'U' : '🍓'}
                    </Avatar>
                    <MessageBubble $isUser={message.isUser}>
                      <MessageText>{message.text}</MessageText>
                      <MessageTime $isUser={message.isUser}>
                        {formatRelativeTime(message.timestamp)}
                      </MessageTime>
                    </MessageBubble>
                  </MessageRow>
                ))}
                
                {(isTyping || showThinking) && (
                  <MessageRow $isUser={false}>
                    <Avatar $isUser={false}>
                      🍓
                    </Avatar>
                    <MessageBubble $isUser={false}>
                      <MessageText>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Spinner size="20px" />
                          <span>Thinking...</span>
                        </div>
                      </MessageText>
                    </MessageBubble>
                  </MessageRow>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </MessagesContainer>
          
          <form onSubmit={handleSubmit}>
            <InputContainer>
              <TextInput
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Type your message here..."
                disabled={isSubmitting}
                rows={1}
              />
              <ButtonContainer>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!inputValue.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="16px" style={{ marginRight: '8px' }} />
                      Sending...
                    </>
                  ) : 'Send'}
                </Button>
              </ButtonContainer>
            </InputContainer>
          </form>
        </ChatContainer>
      )}
    </Card>
  );
};

export default ChatInterface;
